
import React, { useEffect, useState, useCallback } from 'react';
import SingleDocUpload from './Admin';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FaUpload, 
  FaFile, 
  FaSpinner, 
  FaChevronLeft, 
  FaChevronRight,
  FaExclamationTriangle,
  FaBook,
  FaEye,
  FaDownload,
  FaExternalLinkAlt
} from 'react-icons/fa';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/user`;

const ManageDoc = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [docData, setDocData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);


    const handleOpenModalForAdd = () => {
        setError('');
        setIsModalOpen(true);
    };
    const handleCloseModal = () => setIsModalOpen(false);

    const handleDocumentAction = (document) => {
        console.log('Document data:', document); // Debug log to see what data is available
        
        // Check if document has a URL or file path
        if (document.url || document.fileUrl || document.s3Url || document.filePath) {
            // Open document in new tab
            const url = document.url || document.fileUrl || document.s3Url || document.filePath;
            window.open(url, '_blank');
            toast.success('Opening document...');
        } else if (document.filename) {
            // Try to construct a download URL or show file info
            console.log('Attempting to handle file:', document.filename);
            
            // For now, show a more helpful message with the available data
            toast.info(`Document: ${document.filename}\nLesson: ${document.lessonNumber}\n\nNote: The document download functionality needs to be implemented in the backend. The S3 URL is not currently stored in the metadata.`, {
                autoClose: 6000,
                hideProgressBar: false,
            });
        } else {
            // Show toast if no action available
            toast.warning('Document preview not available for this file.');
        }
    };

    const fetchAllDocs = useCallback(async (page, limit) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}/all-doc-data?page=${page}&limit=${limit}`);
            if (response?.data) {
                setDocData(response.data.data || []);
                setCurrentPage(response.data.page || 1);
                setTotalPages(response.data.totalPages || 0);
                setTotalItems(response.data.totalItems || 0);
                 if (response.data.data.length === 0 && response.data.totalItems > 0 && page > 1) {
                    setCurrentPage(prev => Math.max(1, prev -1));
                }
            } else {
                setDocData([]);
                setTotalPages(0);
                setTotalItems(0);
                setError('No data received from server.');
            }
        } catch (err) {
            console.error("Fetch error:", err);
            setDocData([]);
            setTotalPages(0);
            setTotalItems(0);
            setError(err.response?.data?.message || err.message || 'Failed to fetch documents.');
        } finally {
            setIsLoading(false);
        }
    }, [itemsPerPage]); // itemsPerPage is a dependency if it can change, API_BASE_URL is stable

    // Search functionality
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredData(docData);
        } else {
            const filtered = docData.filter(doc => 
                doc.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                doc.lessonNumber?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredData(filtered);
        }
    }, [searchTerm, docData]);

    useEffect(() => {
        fetchAllDocs(currentPage, itemsPerPage);
    }, [currentPage, fetchAllDocs]); // Removed itemsPerPage from here as it's in fetchAllDocs deps

    const handleUploadSuccess = () => {
        console.log("Document uploaded successfully via modal!");
        fetchAllDocs(1, itemsPerPage); // Always fetch page 1 after upload to see new item
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage && !isLoading) {
            setCurrentPage(newPage);
        }
    };
    
    // Enhanced pagination controls with Apple-like design
    const PaginationControls = () => {
        if (!totalPages || totalPages <= 1) return null;

        const getPageNumbers = () => {
            const pageNumbers = [];
            const maxPagesToShow = 5;
            const halfMaxPages = Math.floor(maxPagesToShow / 2);

            if (totalPages <= maxPagesToShow) {
                for (let i = 1; i <= totalPages; i++) {
                    pageNumbers.push(i);
                }
            } else {
                let startPage = currentPage - halfMaxPages;
                let endPage = currentPage + halfMaxPages;

                if (startPage <= 0) {
                    endPage -= (startPage - 1);
                    startPage = 1;
                }
                if (endPage > totalPages) {
                    startPage -= (endPage - totalPages);
                    endPage = totalPages;
                }
                startPage = Math.max(startPage, 1);

                if (startPage > 1) {
                    pageNumbers.push(1);
                    if (startPage > 2) {
                        pageNumbers.push('...');
                    }
                }
                for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(i);
                }
                if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                        pageNumbers.push('...');
                    }
                    pageNumbers.push(totalPages);
                }
            }
            return pageNumbers;
        };

        return (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
                    </div>
                    
                    <nav className="flex items-center gap-1" aria-label="Pagination">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || isLoading}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            <FaChevronLeft className="w-4 h-4" />
                            Previous
                        </button>
                        
                        <div className="flex items-center gap-1">
                            {getPageNumbers().map((page, index) => (
                                typeof page === 'number' ? (
                                    <button
                                        key={index}
                                        onClick={() => handlePageChange(page)}
                                        disabled={isLoading}
                                        className={`inline-flex items-center justify-center w-10 h-10 text-sm font-medium rounded-lg transition-colors duration-200 ${
                                            currentPage === page 
                                                ? 'bg-blue-600 text-white shadow-lg' 
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ) : (
                                    <span key={index} className="inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-gray-500 dark:text-gray-400">
                                        {page}
                                    </span>
                                )
                            ))}
                        </div>
                        
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || isLoading}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            Next
                            <FaChevronRight className="w-4 h-4" />
                        </button>
                    </nav>
                </div>
            </div>
        );
    };


    return (
        <div className="min-h-full">
            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            Document Management
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Upload and manage your educational content
                        </p>
                    </div>
                    <button
                        onClick={handleOpenModalForAdd}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    >
                        <FaUpload className="w-5 h-5" />
                        Upload Document
                    </button>
                </div>

                {/* Search and Filter Bar */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                        <FaFile className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search documents..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-10 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                title="Clear search"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal for Uploading */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl">
                        <SingleDocUpload
                            onClose={handleCloseModal}
                            onUploadSuccess={handleUploadSuccess}
                            isModal={true}
                        />
                    </div>
                </div>
            )}
            
            {/* Error Display */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Documents Grid */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                {isLoading && docData.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full mb-4">
                            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Loading Documents</h3>
                        <p className="text-gray-600 dark:text-gray-400">Please wait while we fetch your documents...</p>
                    </div>
                ) : !isLoading && filteredData.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                            <FaFile className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {searchTerm.trim() ? 'No Documents Match Your Search' : 'No Documents Found'}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {searchTerm.trim() 
                                ? `No documents found matching "${searchTerm}". Try a different search term.`
                                : 'Get started by uploading your first document'
                            }
                        </p>
                        <button
                            onClick={handleOpenModalForAdd}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors duration-200"
                        >
                            <FaUpload className="w-5 h-5" />
                            Upload Document
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Table Header */}
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Documents ({searchTerm.trim() ? filteredData.length : totalItems})
                                    {searchTerm.trim() && ` of ${totalItems}`}
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Table Content */}
                        <div className="overflow-x-auto overflow-y-auto max-h-96 w-full -mx-4 sm:mx-0">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" >
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr className='whitespace-nowrap'>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            #
                                        </th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Lesson
                                        </th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Document Name
                                        </th>
                                        <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {filteredData.map((item, index) => (
                                        <tr key={`${item.filename}-${item.lessonNumber}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200">
                                                    Lesson {item?.lessonNumber}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-shrink-0">
                                                        <FaFile className="w-5 h-5 text-gray-400" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                            {item?.filename}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button 
                                                        onClick={() => handleDocumentAction(item)}
                                                        className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-150 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                        title="View/Download Document"
                                                    >
                                                        <FaEye className="w-4 h-4" />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDocumentAction(item)}
                                                        className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors duration-150 p-1 rounded hover:bg-green-50 dark:hover:bg-green-900/20"
                                                        title="Download Document"
                                                    >
                                                        <FaDownload className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <PaginationControls />
                    </>
                )}
            </div>
        </div>
    );
}

export default ManageDoc;